import { useEffect } from 'react';
import type {
  Project,
  UpdateProjectRequest,
  BookFontId,
  FontScale,
  FontWeightStyle,
} from '@historyjki/shared';
import {
  FONT_SCALE_OPTIONS,
  FORMAT_PRESETS,
  SPREAD_DESIGN_W,
  formatPageInches,
  textSizeForFontScale,
} from '@historyjki/shared';
import { BOOK_FONTS, ensureBookFontsRegistered } from '../../utils/bookFonts';

interface TypographySettingsProps {
  project: Project;
  onUpdate: (data: Partial<UpdateProjectRequest>) => void;
}

// Under the Compose model every text block renders in one face+weight
// (the canvas has no title/body distinction), so the options are the two
// weights the book can actually show. The legacy 'mixed' value renders
// identically to 'regular' and is no longer offered.
const WEIGHT_OPTIONS: Array<{ value: FontWeightStyle; label: string }> = [
  { value: 'regular', label: 'Regular' },
  { value: 'bold', label: 'Bold' },
];

// Bounding box the miniature page fits inside. The page keeps the selected
// format's true proportions and the sample text keeps its true size
// relative to the page, so the preview reads as a shrunk physical page.
const PREVIEW_MAX_W = 360;
const PREVIEW_MAX_H = 300;

/** A page is one half of the design spread. */
const PAGE_DESIGN_W = SPREAD_DESIGN_W / 2;

export function TypographySettings({ project, onUpdate }: TypographySettingsProps) {
  useEffect(() => {
    ensureBookFontsRegistered();
  }, []);

  const fontFamily: BookFontId = project.font_family ?? 'atkinson-hyperlegible';
  const fontScale: FontScale = project.font_scale ?? 'medium';
  const weightStyle: FontWeightStyle = project.font_weight_style ?? 'regular';

  const currentFont = BOOK_FONTS.find((f) => f.id === fontFamily) ?? BOOK_FONTS[0];
  const previewWeight = weightStyle === 'bold' ? 700 : 400;
  const textSize = textSizeForFontScale(fontScale);

  // Miniature page in the format's true proportions. Element sizes are
  // design units — px at a PAGE_DESIGN_W-wide page — so scaling by the
  // mini page's width shows the text exactly as large, relative to the
  // page, as it will render on the Compose canvas and in the printed PDF.
  const formatPreset = FORMAT_PRESETS.find((p) => p.value === project.format_preset);
  const { w: pageWIn, h: pageHIn } = formatPageInches(project.format_preset);
  const pxPerIn = Math.min(PREVIEW_MAX_W / pageWIn, PREVIEW_MAX_H / pageHIn);
  const pageWPx = Math.round(pageWIn * pxPerIn);
  const pageHPx = Math.round(pageHIn * pxPerIn);
  const previewFontPx = (textSize * pageWPx) / PAGE_DESIGN_W;
  const printPt = Math.round((textSize * pageWIn * 72) / PAGE_DESIGN_W);
  const formatLabel = formatPreset?.label ?? 'Square 8×8 in (default)';

  return (
    <div className="typography-settings">
      <h4 className="typography-settings__heading">Typography</h4>

      {/* Font family picker */}
      <div className="typography-settings__group">
        <span className="typography-settings__label">Font</span>
        <div className="typography-settings__chips">
          {BOOK_FONTS.map((f) => (
            <button
              key={f.id}
              type="button"
              className={
                'typography-settings__chip' +
                (f.id === fontFamily ? ' typography-settings__chip--selected' : '')
              }
              style={{ fontFamily: f.cssFamily }}
              onClick={() => void onUpdate({ font_family: f.id })}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Text size */}
      <div className="typography-settings__group">
        <span className="typography-settings__label">Text size</span>
        <div className="typography-settings__segmented">
          {FONT_SCALE_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                'typography-settings__seg-btn' +
                (opt.value === fontScale ? ' typography-settings__seg-btn--active' : '')
              }
              onClick={() => void onUpdate({ font_scale: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
        <p className="typography-settings__hint">
          Every text block on the Compose canvas starts at this size. Use the
          A− / A+ buttons on a block only for deliberate exceptions.
        </p>
      </div>

      {/* Weight style */}
      <div className="typography-settings__group">
        <span className="typography-settings__label">Weight</span>
        <div className="typography-settings__segmented">
          {WEIGHT_OPTIONS.map((opt) => (
            <button
              key={opt.value}
              type="button"
              className={
                'typography-settings__seg-btn' +
                ((opt.value === weightStyle ||
                  (opt.value === 'regular' && weightStyle === 'mixed'))
                  ? ' typography-settings__seg-btn--active'
                  : '')
              }
              onClick={() => void onUpdate({ font_weight_style: opt.value })}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      {/* Page-scale preview: the format's page in miniature, text at its
          true size relative to that page. */}
      <div className="typography-settings__group">
        <span className="typography-settings__label">On your page</span>
        <div className="typography-settings__page-stage">
          <div
            className="typography-settings__page"
            style={{ width: pageWPx, height: pageHPx, fontFamily: currentFont.cssFamily }}
          >
            <div
              className="typography-settings__page-text"
              style={{
                fontSize: `${previewFontPx}px`,
                lineHeight: 1.42,
                fontWeight: previewWeight,
              }}
            >
              <p>The little bear set off on a great adventure through the enchanted forest.</p>
              <p>Every tree whispered secrets and every stone had a story to tell.</p>
            </div>
          </div>
        </div>
        <p className="typography-settings__hint">
          {formatLabel} page shown to scale — about {printPt}&nbsp;pt in the
          printed book.
        </p>
      </div>
    </div>
  );
}
